import React, { useState, useEffect } from 'react';
import { empreendimentoService } from '../../../services/empreendimentoService';
import { movimentacaoService } from '../../../services/movimentacaoService';
import { Empreendimento, ParteEmpreendimento } from '../../../types/empreendimento';
import { MovimentacaoUnidadeCreate, CamposMovimentacaoUnidade, MovimentacaoEmpreendimentoCreate, CamposMovimentacaoEmpreendimento } from '../../../types/movimentacao';
import { Building, X, Save, Home, Edit } from 'lucide-react';

// --- Tipos e Interfaces ---

interface UnidadeComStatus extends ParteEmpreendimento {
    movimentada: boolean;
}

interface Torre {
    id: number;
    nome: string;
    unidadesMatrix: (UnidadeComStatus | null)[][];
    maxAndar: number;
    maxApt: number;
}

type MovimentacaoModalProps = {
    isOpen: boolean;
    onClose: () => void;
    empreendimento: Empreendimento | null;
    onSuccess: () => void;
};

// --- Componente Auxiliar: Modal de Edição da UNIDADE (Permanece o mesmo) ---

const EditorUnidadeModal: React.FC<{
    unidade: UnidadeComStatus;
    onClose: () => void;
    onMovimentacaoSuccess: (movements: MovimentacaoUnidadeCreate[], unidadeAtualizada: UnidadeComStatus) => void;
}> = ({ unidade, onClose, onMovimentacaoSuccess }) => {
    const [unidadeLocal, setUnidadeLocal] = useState(unidade);
    const [novosValores, setNovosValores] = useState<Partial<ParteEmpreendimento>>({});

    const camposEditaveis = [
        { key: 'disponibilidade', label: 'Disponibilidade', tipo: 'select', enum: CamposMovimentacaoUnidade.DISPONIBILIDADE, options: [{value: 1, label: 'Disponível'}, {value: 2, label: 'Vendido'}, {value: 3, label: 'Distratado'}, {value: 4, label: 'Bloqueada'}]},
        { key: 'valor_imovel', label: 'Valor do Imóvel', tipo: 'text', enum: CamposMovimentacaoUnidade.VALOR },
        { key: 'forma_pagamento', label: 'Forma de Pagamento', tipo: 'text', enum: CamposMovimentacaoUnidade.FORMA_PAGAMENTO },
        { key: 'nome', label: 'Nome/Descrição', tipo: 'text', enum: null },
        { key: 'area_privativa_m2', label: 'Área Privativa (m²)', tipo: 'text', enum: null },
        { key: 'quantidade_garagem', label: 'Vagas de Garagem', tipo: 'number', enum: null },
    ];

    const handleConfirmarAlteracoes = () => {
        const alteracoes = Object.keys(novosValores) as (keyof ParteEmpreendimento)[];
        const stagedMovements: MovimentacaoUnidadeCreate[] = [];
        let unidadeAtualizada = { ...unidadeLocal };

        for (const key of alteracoes) {
            const valorNovo = novosValores[key];
            const valorAntigo = unidadeLocal[key];

            if (String(valorNovo) !== String(valorAntigo)) {
                const campoConfig = camposEditaveis.find(c => c.key === key);
                if (campoConfig && campoConfig.enum) {
                    const payload: MovimentacaoUnidadeCreate = {
                        unidade: unidadeLocal.id,
                        campo_alterado: campoConfig.enum,
                        valor_antigo: String(valorAntigo ?? ''),
                        valor_novo: String(valorNovo ?? ''),
                    };
                    stagedMovements.push(payload);
                    unidadeAtualizada = { ...unidadeAtualizada, [key]: valorNovo };
                }
            }
        }

        if (stagedMovements.length === 0) {
            alert("Nenhuma alteração com rastreamento de movimentação foi detectada.");
            // Mesmo sem movimentações, podemos fechar e atualizar a UI se outros campos mudaram
            onMovimentacaoSuccess([], unidadeAtualizada);
            onClose();
            return;
        }

        onMovimentacaoSuccess(stagedMovements, { ...unidadeAtualizada, movimentada: true });
        onClose();
    };

    const handleInputChange = (key: keyof ParteEmpreendimento, value: any) => {
        setNovosValores(prev => ({ ...prev, [key]: value }));
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full flex flex-col max-h-[90vh]">
                <div className="px-6 py-4 border-b flex justify-between items-center"><h3 className="text-xl font-semibold text-gray-900">Movimentar Unidade: {unidade.nome}</h3> <button onClick={onClose} className="text-gray-500 hover:text-gray-800"><X size={24} /></button></div>
                <div className="p-6 overflow-y-auto space-y-4">
                    {camposEditaveis.map(({ key, label, tipo, options }) => (
                        <div key={key} className="grid grid-cols-10 gap-4 items-center">
                            <label className="col-span-3 text-sm font-medium text-gray-700">{label}</label>
                            <div className="col-span-3 text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-md truncate">{String(unidadeLocal[key as keyof ParteEmpreendimento] ?? 'Não definido')}</div>
                            <div className="col-span-4">
                                {tipo === 'boolean' ? (<select value={String(novosValores[key as keyof ParteEmpreendimento] ?? unidadeLocal[key as keyof ParteEmpreendimento] ?? 'false')} onChange={e => handleInputChange(key as keyof ParteEmpreendimento, e.target.value === 'true')} className="w-full px-3 py-2 border border-gray-300 rounded-md"><option value="true">Sim</option><option value="false">Não</option></select>)
                                : tipo === 'select' ? (<select value={String(novosValores[key as keyof ParteEmpreendimento] ?? unidadeLocal[key as keyof ParteEmpreendimento] ?? '')} onChange={e => handleInputChange(key as keyof ParteEmpreendimento, e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md">{options?.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}</select>)
                                : (<input type={tipo} placeholder="Novo Valor" value={String(novosValores[key as keyof ParteEmpreendimento] ?? '')} onChange={e => handleInputChange(key as keyof ParteEmpreendimento, e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md"/>)}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="px-6 py-4 bg-gray-50 border-t flex justify-end">
                    <button onClick={handleConfirmarAlteracoes} className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700">
                        Confirmar Alterações
                    </button>
                </div>
            </div>
        </div>
    );
};


// --- Componente Principal (MODIFICADO) ---





const MovimentacaoModal: React.FC<MovimentacaoModalProps> = ({ isOpen, onClose, empreendimento: initialEmpreendimento, onSuccess }) => {


    const [empreendimento, setEmpreendimento] = useState(initialEmpreendimento);


    const [torres, setTorres] = useState<Torre[]>([]);


    const [unidadesAvulsas, setUnidadesAvulsas] = useState<UnidadeComStatus[]>([]);


    const [activeTab, setActiveTab] = useState(0);


    const [loading, setLoading] = useState(false);


    const [unidadeParaEditar, setUnidadeParaEditar] = useState<UnidadeComStatus | null>(null);





    // --- REESTRUTURAÇÃO DO ESTADO PARA SALVAMENTO ÚNICO ---


    const [stagedEmpreendimentoMovements, setStagedEmpreendimentoMovements] = useState<MovimentacaoEmpreendimentoCreate[]>([]);


    const [stagedUnidadeMovements, setStagedUnidadeMovements] = useState<MovimentacaoUnidadeCreate[]>([]);

    // Arquivos do empreendimento (tabela de preço, book, extras). Cada um vira
    // sua própria movimentação (campo_alterado correto + arquivo no índice
    // certo), nunca coladas à força na primeira movimentação staged.
    const [arquivosEmpreendimento, setArquivosEmpreendimento] = useState<Record<string, File | null>>({});
    const [isSaving, setIsSaving] = useState(false);

    const arquivosEmpreendimentoConfig: { key: 'tabela_preco' | 'book' | 'arquivo_extra_1' | 'arquivo_extra_2'; label: string; enum: CamposMovimentacaoEmpreendimento }[] = [
        { key: 'tabela_preco', label: 'Tabela de Preço', enum: CamposMovimentacaoEmpreendimento.TABELA_PRECO },
        { key: 'book', label: 'Book', enum: CamposMovimentacaoEmpreendimento.BOOK },
        { key: 'arquivo_extra_1', label: 'Arquivo Extra 1', enum: CamposMovimentacaoEmpreendimento.ARQUIVO_EXTRA_1 },
        { key: 'arquivo_extra_2', label: 'Arquivo Extra 2', enum: CamposMovimentacaoEmpreendimento.ARQUIVO_EXTRA_2 },
    ];

    const handleArquivoEmpreendimentoChange = (key: string, file: File | null) => {
        setArquivosEmpreendimento(prev => ({ ...prev, [key]: file }));
    };





    const camposEmpreendimentoEditaveis = [


        { key: 'area_construida', label: 'Área Construída', tipo: 'number', enum: CamposMovimentacaoEmpreendimento.AREA_CONSTRUIDA },


        { key: 'data_entrega', label: 'Data de Entrega', tipo: 'date', enum: CamposMovimentacaoEmpreendimento.DATA_ENTREGA },


        { key: 'data_inicio_comercializacao', label: 'Data Início Comercialização', tipo: 'date', enum: CamposMovimentacaoEmpreendimento.DATA_INICIO_COMERCIALIZACAO },


        { key: 'observacao', label: 'Observação', tipo: 'text', enum: CamposMovimentacaoEmpreendimento.OBSERVACAO },


        { key: 'fase_empreendimento', label: 'Fase do Empreendimento', tipo: 'select', enum: CamposMovimentacaoEmpreendimento.FASE_EMPREENDIMENTO, options: [{value: 1, label: 'Planta'}, {value: 2, label: 'Obras'}, {value: 3, label: 'Pronto'}] },


    ];





    // Adiciona ou atualiza uma movimentação de empreendimento na lista de staged


    const stageEmpreendimentoMovement = (key: keyof Empreendimento, value: any) => {


        if (!empreendimento) return;





        const campoConfig = camposEmpreendimentoEditaveis.find(c => c.key === key);


        if (!campoConfig || !campoConfig.enum) return;


        


        const valorAntigo = empreendimento[key];





        const newMovement: MovimentacaoEmpreendimentoCreate = {


            empreendimento: empreendimento.id,


            campo_alterado: campoConfig.enum,


            valor_antigo: String(valorAntigo ?? ''),


            valor_novo: String(value ?? ''),


        };





        setStagedEmpreendimentoMovements(prev => {


            const existingIndex = prev.findIndex(m => m.campo_alterado === newMovement.campo_alterado);


            if (existingIndex > -1) {


                const updated = [...prev];


                updated[existingIndex] = newMovement;


                return updated;


            }


            return [...prev, newMovement];


        });


    };





    // Salva TODAS as movimentações (empreendimento e unidades) de uma vez


    const handleSaveAll = async () => {

        // Cada arquivo selecionado vira sua própria movimentação, com o
        // campo_alterado correto e o arquivo anexado diretamente nela — nada
        // de colar no índice 0 de outra movimentação qualquer.
        const movimentosDeArquivo: MovimentacaoEmpreendimentoCreate[] = arquivosEmpreendimentoConfig
            .filter(cfg => arquivosEmpreendimento[cfg.key])
            .map(cfg => ({
                empreendimento: empreendimento!.id,
                campo_alterado: cfg.enum,
                valor_antigo: String((empreendimento as any)?.[cfg.key] ?? ''),
                valor_novo: arquivosEmpreendimento[cfg.key]!.name,
                [cfg.key]: arquivosEmpreendimento[cfg.key] as File,
            }));

        const todasMovimentacoesEmpreendimento = [...stagedEmpreendimentoMovements, ...movimentosDeArquivo];

        if (todasMovimentacoesEmpreendimento.length === 0 && stagedUnidadeMovements.length === 0) {


            alert("Nenhuma alteração para salvar.");


            return;


        }





        setIsSaving(true);


        try {


            await movimentacaoService.createUnifiedMovimentacao({
                movimentacoes_empreendimento: todasMovimentacoesEmpreendimento,
                movimentacoes_unidade: stagedUnidadeMovements,
            });


            alert('Todas as alterações foram salvas com sucesso!');


            if (onSuccess) onSuccess();


            onClose();


        } catch (error) {


            console.error("Erro ao salvar todas as movimentações:", error);


            alert("Ocorreu um erro ao salvar as alterações.");


        } finally {


            setIsSaving(false);


        }


    };


    


    // Recebe as movimentações da unidade e as adiciona ao staged


    const handleUnidadeMovimentacao = (movements: MovimentacaoUnidadeCreate[], unidadeAtualizada: UnidadeComStatus) => {


        setStagedUnidadeMovements(prev => {


            // Remove movimentos antigos para esta unidade e adiciona os novos


            const otherMovements = prev.filter(m => m.unidade !== unidadeAtualizada.id);


            return [...otherMovements, ...movements];


        });


        // Atualiza a UI localmente para refletir o novo estado


        handleUnidadeAtualizada(unidadeAtualizada);


        setUnidadeParaEditar(null);


    };





    // --- FIM DAS ALTERAÇÕES DE LÓGICA ---








    useEffect(() => {


        if (initialEmpreendimento) {


            setEmpreendimento(initialEmpreendimento);


            setStagedEmpreendimentoMovements([]);


            setStagedUnidadeMovements([]);


            setArquivosEmpreendimento({});


        }


    }, [initialEmpreendimento]);





    useEffect(() => {


        if (!isOpen || !empreendimento) {


            setTorres([]);


            setUnidadesAvulsas([]);


            return;


        }





        const fetchDataAndBuildMatrix = async () => {


            setLoading(true);


            try {


                const todasAsUnidades = await empreendimentoService.listPartesEmpreendimento(empreendimento.id);


                console.log("todasAsUnidades:", todasAsUnidades); // DEBUG LOG


                const unidadesComStatus: UnidadeComStatus[] = todasAsUnidades.map(u => ({ ...u, movimentada: false }));


                console.log("unidadesComStatus:", unidadesComStatus); // DEBUG LOG


                const avulsas = unidadesComStatus.filter(u => !u.torre);


                const comTorre = unidadesComStatus.filter(u => !!u.torre);


                console.log("avulsas:", avulsas); // DEBUG LOG


                console.log("comTorre:", comTorre); // DEBUG LOG


                setUnidadesAvulsas(avulsas);


                const torresMap: Record<string, UnidadeComStatus[]> = {};


                comTorre.forEach(unidade => {


                    const torreNome = unidade.torre_texto || `Torre ${unidade.torre}`;


                    if (!torresMap[torreNome]) torresMap[torreNome] = [];


                    torresMap[torreNome].push(unidade);


                });


                const torresEstruturadas: Torre[] = Object.entries(torresMap).map(([nome, unidades], index) => {


                    let maxAndar = 0, maxApt = 0;


                    unidades.forEach(u => { if (u.andar > maxAndar) maxAndar = u.andar; if (u.numero_apartamento > maxApt) maxApt = u.numero_apartamento; });


                    const unidadesMatrix: (UnidadeComStatus | null)[][] = Array(maxAndar).fill(0).map(() => Array(maxApt).fill(null));


                    unidades.forEach(u => { const andarIndex = maxAndar - u.andar; const aptIndex = u.numero_apartamento - 1; if (andarIndex >= 0 && aptIndex >= 0) { unidadesMatrix[andarIndex][aptIndex] = u; } });


                    return { id: index, nome, unidadesMatrix, maxAndar, maxApt };


                });


                console.log("torresEstruturadas:", torresEstruturadas); // DEBUG LOG


                setTorres(torresEstruturadas);


            } catch (error) {


                console.error("Erro ao montar matriz de unidades:", error);


                alert("Falha ao carregar as unidades do empreendimento.");


            } finally { setLoading(false); }


        };


        fetchDataAndBuildMatrix();


    }, [isOpen, empreendimento]);


    


    const handleUnidadeAtualizada = (unidadeAtualizada: UnidadeComStatus) => {


        setUnidadeParaEditar(unidadeAtualizada);


        setTorres(prev => prev.map(torre => ({ ...torre, unidadesMatrix: torre.unidadesMatrix.map(andar => andar.map(un => (un?.id === unidadeAtualizada.id ? unidadeAtualizada : un))) })));


        setUnidadesAvulsas(prev => prev.map(un => (un.id === unidadeAtualizada.id ? unidadeAtualizada : un)));


    };





    if (!isOpen || !empreendimento) return null;





    return (


        <>


            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">


                <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-full flex flex-col">


                    


                    <div className="px-6 py-4 border-b flex justify-between items-center flex-shrink-0">


                        <h2 className="text-2xl font-bold text-gray-800">Painel de Movimentação - {empreendimento.nome}</h2>


                        <button onClick={onClose} className="text-gray-500 hover:text-gray-800"><X size={28} /></button>


                    </div>





                    <div className="flex-grow overflow-y-auto">


                        


                        <div className="p-6 border-b">


                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Movimentar Dados do Empreendimento</h3>


                            <div className="space-y-4">


                                {camposEmpreendimentoEditaveis.map(({ key, label, tipo, options }) => (


                                    <div key={key} className="grid grid-cols-10 gap-4 items-center">


                                        <label className="col-span-3 text-sm font-medium text-gray-700">{label}</label>


                                        <div className="col-span-3 text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-md truncate">


                                            {String(empreendimento[key as keyof Empreendimento] ?? 'Não definido')}


                                        </div>


                                        <div className="col-span-4">


                                            {tipo === 'select' ? (


                                                <select


                                                    value={stagedEmpreendimentoMovements.find(m => m.campo_alterado === camposEmpreendimentoEditaveis.find(c => c.key === key)?.enum)?.valor_novo ?? String(empreendimento[key as keyof Empreendimento] ?? '')}


                                                    onChange={e => stageEmpreendimentoMovement(key as keyof Empreendimento, e.target.value)}


                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"


                                                >


                                                    {options?.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}


                                                </select>


                                            ) : (


                                                <input


                                                    type={tipo}


                                                    placeholder="Novo Valor"


                                                    value={stagedEmpreendimentoMovements.find(m => m.campo_alterado === camposEmpreendimentoEditaveis.find(c => c.key === key)?.enum)?.valor_novo ?? ''}


                                                    onChange={e => stageEmpreendimentoMovement(key as keyof Empreendimento, e.target.value)}


                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"


                                                />


                                            )}


                                        </div>


                                    </div>


                                ))}


                            </div>


                        </div>





                        <div className="p-6">


                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Movimentar Unidades</h3>


                            {loading ? <p>Carregando unidades...</p> : (


                                <div className="flex flex-col min-h-0">


                                    <div className="border-b border-gray-200">


                                        <nav className="-mb-px flex space-x-6">


                                            {torres.map((torre, index) => (<button key={torre.id} onClick={() => setActiveTab(index)} className={`whitespace-nowrap inline-flex items-center py-3 px-1 border-b-2 font-medium text-sm ${activeTab === index ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:border-gray-300'}`}><Building size={16} className="mr-2" /> {torre.nome} </button>))}


                                            {unidadesAvulsas.length > 0 && (<button onClick={() => setActiveTab(torres.length)} className={`whitespace-nowrap inline-flex items-center py-3 px-1 border-b-2 font-medium text-sm ${activeTab === torres.length ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:border-gray-300'}`}><Home size={16} className="mr-2" /> Unidades Avulsas ({unidadesAvulsas.length})</button>)}


                                        </nav>


                                    </div>


                                    <div className="pt-4">


                                        {activeTab < torres.length && torres[activeTab] ? (


                                            <table className="min-w-full border-collapse">


                                                <thead><tr><th className="p-2 border text-xs font-bold text-gray-600 bg-gray-100">Andar</th>{Array.from({ length: torres[activeTab].maxApt }).map((_, i) => (<th key={i} className="p-2 border text-xs font-bold text-gray-600 bg-gray-100">Apto {i + 1}</th>))}</tr></thead>


                                                <tbody>{torres[activeTab].unidadesMatrix.map((andar, rowIndex) => (<tr key={rowIndex}><td className="p-2 border font-semibold text-center bg-gray-50">{andar.find(u => u && u.pavimento_texto)?.pavimento_texto || `${torres[activeTab].maxAndar - rowIndex}º Andar`}</td>{Array.from({ length: torres[activeTab].maxApt }).map((_, aptIndex) => { const unidade = andar[aptIndex]; return (<td key={aptIndex} className="p-1 border text-center">{unidade ? (<button onClick={() => setUnidadeParaEditar(unidade)} className={`w-full h-16 rounded flex flex-col items-center justify-center text-white font-bold text-xs p-1 transition-transform hover:scale-105 ${stagedUnidadeMovements.some(m => m.unidade === unidade.id) ? 'bg-orange-500' : (unidade.movimentada ? 'bg-green-600' : 'bg-red-500')}`}> <Building size={20} className="mr-2" /><span>{unidade.nome}</span> </button>) : <div className="w-full h-16 bg-gray-200 rounded"></div>}</td>);})}</tr>))}</tbody>


                                            </table>


                                        ) : (


                                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">{unidadesAvulsas.map(unidade => (<button key={unidade.id} onClick={() => setUnidadeParaEditar(unidade)} className={`h-24 rounded flex flex-col items-center justify-center text-white font-bold text-sm p-2 transition-transform hover:scale-105 ${stagedUnidadeMovements.some(m => m.unidade === unidade.id) ? 'bg-orange-500' : (unidade.movimentada ? 'bg-green-600' : 'bg-red-500')}`}><Home size={20} className="mb-1"/><span>{unidade.nome}</span><span className="font-normal opacity-80"></span></button>))}</div>


                                        )}


                                    </div>


                                </div>


                            )}


                                                </div>


                        


                        


                        


                        


                        


                                            </div>


                        


                        


                        


                        


                        


                                            <div className="p-6 border-b">


                                                <h3 className="text-lg font-semibold text-gray-800 mb-4">Documentos (Tabela de Preço, Book e Extras)</h3>
                                                <p className="text-sm text-gray-500 mb-2">Cada arquivo enviado vira uma movimentação própria, com data.</p>


                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {arquivosEmpreendimentoConfig.map(cfg => (
                                                        <div key={cfg.key}>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">{cfg.label}</label>
                                                            <input
                                                                type="file"
                                                                onChange={e => handleArquivoEmpreendimentoChange(cfg.key, e.target.files?.[0] || null)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                            />
                                                            {arquivosEmpreendimento[cfg.key] && (
                                                                <p className="mt-1 text-xs text-gray-600">Selecionado: {arquivosEmpreendimento[cfg.key]!.name}</p>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>


                                            </div>





                                            <div className="px-6 py-4 bg-gray-50 border-t flex justify-between items-center flex-shrink-0">


                        <span className="text-sm text-gray-600">


                            {stagedEmpreendimentoMovements.length + stagedUnidadeMovements.length + Object.values(arquivosEmpreendimento).filter(Boolean).length} alteraçõe(s) pendente(s).


                        </span>


                        <button 


                            onClick={handleSaveAll}


                            disabled={isSaving} 


                            className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white font-semibold rounded-md hover:bg-indigo-700 disabled:bg-indigo-300"


                        >


                            <Save size={16} />


                            {isSaving ? 'Salvando...' : 'Salvar Todas as Alterações'}


                        </button>


                    </div>





                </div>


            </div>





            {unidadeParaEditar && (


                <EditorUnidadeModal unidade={unidadeParaEditar} onClose={() => setUnidadeParaEditar(null)} onMovimentacaoSuccess={handleUnidadeMovimentacao}/>


            )}


        </>


    );


};





export default MovimentacaoModal;